<template>
  <ChartSkeleton
    v-if="loading"
    :style="{ width, height, minWidth, minHeight, maxWidth, maxHeight }"
    :type="type"
    :scale="skeletonScale"
  />
  <NoDataPannel v-else-if="!hasData" :style="{ width, height, minWidth, minHeight, maxWidth, maxHeight }" />
  <div v-else :style="{ width, height, minWidth, minHeight, maxWidth, maxHeight }">
    <apexchart
      :type="type"
      :class="{ 'chart-donus': type === 'donut' }"
      width="100%"
      :height="isEmpty(chartHeight) ? '100%' : chartHeight"
      :options="chartOptions"
      :series="series"
      @mounted="handleBuildTooltips"
      @updated="handleBuildTooltips"
    />
  </div>
</template>

<script setup lang="ts">
import ChartSkeleton from './skeletons/ChartSkeleton.vue';
import NoDataPannel from './NoDataPannel.vue';
import { computed, inject, onUnmounted, onUpdated, ref } from 'vue';
import type { Config } from 'tailwindcss';
import { computeChartOptions } from '../app-constants/chart.constant';
import { isArray, isEmpty, isObject } from 'lodash';
import { overrideAxisTooltips, overrideLegendTooltips } from './chart.utils';

const theme: Config = inject('theme');
const props = defineProps({
  overrideXAxisTooltip: {
    type: Boolean,
    default: true,
  },
  overrideYAxisTooltip: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'line',
  },
  options: {
    type: Object,
    default: () => ({}),
  },
  series: {
    type: Array,
    default: () => [],
  },
  width: {
    type: String,
    default: '100%',
  },
  height: {
    type: String,
    default: 'auto',
  },
  minWidth: {
    type: String,
    default: undefined,
  },
  minHeight: {
    type: String,
    default: undefined,
  },
  maxWidth: {
    type: String,
    default: undefined,
  },
  maxHeight: {
    type: String,
    default: undefined,
  },
  skeletonScale: {
    type: String,
    default: '1 1',
  },
  chartHeight: {
    type: String,
    default: undefined,
  },
});

const chartOptions = computed(() => {
  return computeChartOptions(props.options, theme, props.type);
});

const hasData = computed(() => {
  if (isEmpty(props.series)) {
    return false;
  }
  if (isArray(props.series)) {
    const firstSeri = props.series[0] as object;
    if (isObject(firstSeri) && 'data' in firstSeri) {
      if (isEmpty(firstSeri.data)) {
        return false;
      }
    }
  }
  return true;
});

const tooltipUnsubscribers = ref<(() => void)[]>([]);

const unsubscribeTooltips = (): void => {
  tooltipUnsubscribers.value?.forEach((unsub) => unsub());
  tooltipUnsubscribers.value = [];
};

const handleBuildTooltips = (context): void => {
  const chartEl: HTMLDivElement = context.el;
  const yaxisEl: HTMLElement = chartEl.querySelector('.apexcharts-yaxis');
  if (yaxisEl && props.overrideYAxisTooltip) {
    const [, yaxisTooltipUnsubscribers] = overrideAxisTooltips(yaxisEl);
    tooltipUnsubscribers.value.push(yaxisTooltipUnsubscribers);
  }

  const xaxisEl: HTMLElement = chartEl.querySelector('.apexcharts-xaxis');
  if (xaxisEl && props.overrideXAxisTooltip) {
    const [, xaxisTooltipUnsubscribers] = overrideAxisTooltips(xaxisEl);
    tooltipUnsubscribers.value.push(xaxisTooltipUnsubscribers);
  }

  const legendEl: HTMLElement = chartEl.querySelector('.apexcharts-legend');
  if (legendEl) {
    const [, legendTooltipUnsubscriber] = overrideLegendTooltips(legendEl);
    tooltipUnsubscribers.value.push(legendTooltipUnsubscriber);
  }
};

onUpdated(() => {
  unsubscribeTooltips();
});
onUnmounted(() => {
  unsubscribeTooltips();
});
</script>
<style>
/* 
  Because the donut-chart tooltip is a global component, we need to style it globally.
*/
.donut-chart-tooltip {
  @apply px-3 py-2;
  @apply flex flex-col gap-1;
}

.donut-chart-tooltip .second-line {
  @apply flex gap-2;
}

.donut-chart-tooltip .second-line .percent {
  @apply font-semibold;
}

.donut-chart-tooltip .second-line .value::before {
  display: inline-block;
  content: '(';
}

.donut-chart-tooltip .second-line .value::after {
  content: ')';
  display: inline-block;
}

.apexcharts-pie-series path,
.apexcharts-pie-series path:hover {
  filter: none !important;
}

.apexcharts-tooltip.apexcharts-theme-dark {
  background: rgba(30, 30, 30, 1) !important;
}
</style>
